// TierCheckoutPage — Subscription tier upgrade with side-by-side comparison
// Layout: tier picker (3 cards), feature comparison, payment

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TierChip, PartnerLogo } from '../../components/Brand';

const TIERS = [
  {
    key: 'Beginner',
    name: 'Beginner',
    account: '$50K',
    monthly: 79,
    annual: 790,
    color: '#34C759',
    emoji: '🌱',
    features: [
      { text: '$50K account size', included: true },
      { text: '1 active bot',     included: true },
      { text: 'Up to 4 strategies', included: true },
      { text: 'EOD balance drawdown', included: true },
      { text: '80% profit split (first $5K)', included: true },
      { text: '90% profit split ($5K+)', included: false },
      { text: 'Live newsfeed+',    included: false },
      { text: 'API access',        included: false },
      { text: 'Custom AI strategies', included: false },
    ],
  },
  {
    key: 'Mid',
    name: 'Mid',
    account: '$100K',
    monthly: 149,
    annual: 1490,
    color: '#007AFF',
    emoji: '🚀',
    features: [
      { text: '$100K account size', included: true },
      { text: '3 active bots',     included: true },
      { text: 'All 4 strategies',  included: true },
      { text: 'EOD balance drawdown', included: true },
      { text: '85% profit split',  included: true },
      { text: '90% profit split ($10K+)', included: true },
      { text: 'Live newsfeed+',    included: true },
      { text: 'API access',        included: false },
      { text: 'Custom AI strategies', included: false },
    ],
  },
  {
    key: 'Professional',
    name: 'Professional',
    account: '$150K',
    monthly: 249,
    annual: 2490,
    color: '#1C1C1E',
    emoji: '💎',
    features: [
      { text: '$150K account size', included: true },
      { text: 'Unlimited bots',    included: true },
      { text: 'All strategies + custom', included: true },
      { text: 'EOD balance drawdown', included: true },
      { text: '90% profit split',  included: true },
      { text: '95% profit split ($25K+)', included: true },
      { text: 'Live newsfeed+',    included: true },
      { text: 'API access',        included: true },
      { text: 'Custom AI strategies', included: true },
    ],
  },
];

export function TierCheckoutPage() {
  const nav = useNavigate();
  const [selected, setSelected] = useState<string | null>('Mid');
  const [billing, setBilling] = useState<'monthly' | 'annual'>('monthly');
  const [step, setStep] = useState<'compare' | 'review' | 'done'>('compare');
  const tier = TIERS.find((t) => t.key === selected);
  const price = tier ? (billing === 'monthly' ? tier.monthly : tier.annual) : 0;

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Upgrade plan</div>
        <div className="w-9" />
      </div>

      {/* Step indicator */}
      <div className="px-4 pt-3 flex items-center justify-center gap-1.5">
        {['compare', 'review', 'done'].map((s, i) => (
          <div key={s} className={['h-1 rounded-full transition-all', step === s ? 'w-6 bg-tf-blue' : ['w-1.5', ['review', 'done'].includes(s) ? 'bg-tf-blue' : 'bg-tf-surface-3'].join(' ')].join(' ')} />
        ))}
      </div>

      {step === 'compare' && (
        <>
          {/* Hero */}
          <div className="mx-4 mt-4">
            <div className="ios26-hero text-center" style={{ background: 'linear-gradient(135deg, #1C1C1E 0%, #5856D6 100%)', color: 'white' }}>
              <div className="text-[11px] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.8)' }}>Choose your tier</div>
              <div className="mt-1 text-[26px] font-bold tracking-tight">More capital, more profit</div>
              <div className="mt-1 text-[13px]" style={{ color: 'rgba(255,255,255,0.85)' }}>Upgrade anytime. Cancel anytime.</div>
            </div>
          </div>

          {/* Billing toggle */}
          <div className="mx-4 mt-3 ios26-seg" data-testid="billing-toggle">
            <button onClick={() => setBilling('monthly')} className={billing === 'monthly' ? 'active' : ''}>Monthly</button>
            <button onClick={() => setBilling('annual')} className={billing === 'annual' ? 'active' : ''}>
              Annual <span className="text-tf-profit">-17%</span>
            </button>
          </div>

          {/* Tier picker */}
          <div className="mx-4 mt-3 space-y-2.5">
            {TIERS.map((t) => (
              <button
                key={t.key}
                onClick={() => setSelected(t.key)}
                data-testid={`tier-pick-${t.key}`}
                className={['flex w-full items-start gap-3 rounded-[16px] p-3 text-left transition',
                  selected === t.key ? 'border-2 shadow-sm' : 'border border-tf-border bg-white'
                ].join(' ')}
                style={selected === t.key ? { borderColor: t.color, backgroundColor: t.color + '08' } : {}}
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-[14px] text-[24px]" style={{ backgroundColor: t.color + '20' }}>{t.emoji}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[15px] font-bold text-tf-text-1">{t.name}</span>
                      <TierChip tier={t.key as any} />
                    </div>
                    {selected === t.key && (
                      <svg viewBox="0 0 24 24" className="h-5 w-5" style={{ color: t.color }} fill="currentColor"><path d="M9 16.17 5.53 12.7a1 1 0 0 0-1.41 1.41l4.18 4.18a1 1 0 0 0 1.41 0L20.29 8.71a1 1 0 0 0-1.41-1.41z" /></svg>
                    )}
                  </div>
                  <div className="text-[11px] text-tf-text-3">Account: {t.account}</div>
                  <div className="mt-1 flex items-baseline gap-1">
                    <span className="text-[20px] font-bold tabular-nums text-tf-text-1">${billing === 'monthly' ? t.monthly : t.annual}</span>
                    <span className="text-[11px] text-tf-text-3">/{billing === 'monthly' ? 'mo' : 'yr'}</span>
                    {billing === 'annual' && <span className="text-[10px] text-tf-profit font-bold">save ${(t.monthly * 12 - t.annual)}</span>}
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Feature comparison */}
          <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Feature comparison</h2>
          <div className="mx-4 ios26-list bg-white">
            {TIERS[0].features.map((_, i) => {
              const row = TIERS.map((t) => t.features[i]);
              return (
                <div key={i} className={['flex items-center px-4 py-2.5', i < TIERS[0].features.length - 1 ? 'border-b border-tf-border' : ''].join(' ')}>
                  <div className="flex-1 text-[12px] text-tf-text-1">{row[0].text}</div>
                  {row.map((cell) => (
                    <div key={cell.text} className="w-12 flex justify-center">
                      {cell.included ? (
                        <svg viewBox="0 0 24 24" className="h-4 w-4 text-tf-profit" fill="currentColor"><path d="M9 16.17 5.53 12.7a1 1 0 0 0-1.41 1.41l4.18 4.18a1 1 0 0 0 1.41 0L20.29 8.71a1 1 0 0 0-1.41-1.41z" /></svg>
                      ) : (
                        <span className="text-tf-text-muted">—</span>
                      )}
                    </div>
                  ))}
                </div>
              );
            })}
            {/* Header row */}
            <div className="sticky top-11 -mt-px grid grid-cols-4 border-y border-tf-border bg-tf-surface-1 px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-tf-text-3">
              <div className="text-left">Feature</div>
              <div className="text-center">Beg.</div>
              <div className="text-center">Mid</div>
              <div className="text-center">Pro</div>
            </div>
          </div>

          {/* Continue */}
          <div className="mx-4 mt-4">
            <button onClick={() => setStep('review')} disabled={!selected} data-testid="to-review" className="flex h-12 w-full items-center justify-center btn-spring disabled:opacity-40">
              Continue with {tier?.name}
            </button>
            <p className="mt-2 text-center text-[10px] text-tf-text-3">Cancel anytime · 30-day money back · No hidden fees</p>
          </div>
        </>
      )}

      {step === 'review' && tier && (
        <div className="px-4 pt-4">
          <h2 className="text-[22px] font-bold tracking-tight text-tf-text-1">Review & confirm</h2>
          <p className="mt-1 mb-4 text-[14px] text-tf-text-3">You're upgrading to {tier.name}.</p>

          <div className="rounded-[20px] bg-white p-5 text-center" style={{ borderColor: tier.color, borderWidth: 2 }}>
            <div className="text-[11px] uppercase tracking-wider text-tf-text-3">You'll be charged</div>
            <div className="mt-1 text-[36px] font-bold tabular-nums text-tf-text-1">${price}</div>
            <div className="text-[12px] text-tf-text-3">{billing === 'monthly' ? 'per month' : 'per year'} · {tier.account} account</div>
          </div>

          <h3 className="mt-5 px-1 text-[13px] font-semibold uppercase tracking-wide text-tf-text-3">Payment method</h3>
          <div className="ios26-list bg-white">
            <div className="flex items-center gap-3 px-4 py-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-tf-blue-tint text-tf-blue text-[18px]">💳</div>
              <div className="flex-1">
                <div className="text-[14px] font-semibold text-tf-text-1">Visa •••• 4242</div>
                <div className="text-[11px] text-tf-text-3">Expires 12/27</div>
              </div>
              <span className="rounded-full bg-[#34C759]/15 px-2 py-0.5 text-[10px] font-bold uppercase text-tf-profit">Default</span>
            </div>
            <button className="flex w-full items-center gap-3 border-t border-tf-border px-4 py-3 text-tf-blue active:bg-tf-surface-1">
              <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-tf-blue-tint text-[18px]">+</div>
              <span className="text-[14px] font-semibold">Add payment method</span>
            </button>
          </div>

          <div className="mt-4 rounded-[14px] border border-[#34C759]/30 bg-[#34C759]/[0.04] p-3">
            <div className="flex gap-2.5">
              <span className="text-[18px]">🔒</span>
              <div>
                <div className="text-[13px] font-semibold text-tf-profit">Secure checkout</div>
                <p className="mt-0.5 text-[12px] text-tf-text-1">Powered by <PartnerLogo name="stripe" height={12} className="inline-block align-middle" />. 256-bit encryption. PCI DSS Level 1.</p>
              </div>
            </div>
          </div>

          <button onClick={() => { setStep('done'); }} data-testid="confirm-upgrade" className="mt-4 flex h-12 w-full items-center justify-center btn-spring">
            Confirm and pay ${price}
          </button>
          <button onClick={() => setStep('compare')} className="mt-2 flex h-10 w-full items-center justify-center text-[14px] text-tf-text-3 active:opacity-40">Back</button>
        </div>
      )}

      {step === 'done' && tier && (
        <div className="flex flex-col items-center justify-center pt-20 px-4 text-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[#34C759] text-white">
            <svg viewBox="0 0 24 24" className="h-12 w-12" fill="none" stroke="currentColor" strokeWidth="3"><path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </div>
          <h2 className="mt-6 text-[24px] font-bold tracking-tight text-tf-text-1">You're on {tier.name}!</h2>
          <p className="mt-1 text-[14px] text-tf-text-3">${price} charged · {tier.account} account unlocked</p>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#34C759]/15 px-3 py-1.5">
            <TierChip tier={tier.key as any} />
            <span className="text-[12px] font-semibold text-tf-profit">Active</span>
          </div>
          <button onClick={() => nav('/portal/dashboard')} data-testid="upgrade-done" className="mt-8 h-12 w-full max-w-xs btn-spring">Go to dashboard</button>
        </div>
      )}
    </div>
  );
}
